import { AfterContentInit, Component, Input, OnChanges, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { GeneralEPP } from '../../models/external-pressure-profile.model';
import { isNumber } from 'lodash-es';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { GetMudHydrostaticPressure } from 'src/app/perivis/shared/helpers/mud-hydrostatic.helper';
import { lastValueFrom } from 'rxjs';
import { GetValueFromPpg } from 'src/app/perivis/shared/helpers/units.helper';
import { LoadCaseService } from 'src/app/perivis/services/load-case.service';

interface GeneralEPPForm {
  fluidDensity: number;
  hangerPressure: number;
  hasDeterioratedMud: boolean;
  deterioratedMudDensity: number;
  isLeakAtHanger: boolean;
}

@Component({
    selector: 'app-general-epp',
    templateUrl: './general-epp.component.html',
    providers: subformComponentProviders(GeneralEppComponent),
    standalone: false
})
export class GeneralEppComponent implements OnInit, AfterContentInit, OnChanges {
  public control(name: string): UntypedFormControl {
    return this.form.formGroup.get(name) as UntypedFormControl;
  }
  
  @Input() currentString: any;
  @Input() userUnits: UserUnitsModel;
  @Input() loadCaseIndex: number;
  @Input() hideDeterioratedMudOption = false;

  public topPressureLabel: string;
  public tubingLeakDensity: number;

  private defaultHangerPressure: number;

  public densityLabel: string;
  public pressureLabel: string;
  public pressureValidation: { min: number, max: number };
  public densityValidation: { min: number, max: number };

  public form = createForm<GeneralEPP, GeneralEPPForm>(this, {
    formType: FormType.SUB,
    formControls: {
      fluidDensity: new UntypedFormControl(null),
      hangerPressure: new UntypedFormControl(null),
      hasDeterioratedMud: new UntypedFormControl(null),
      deterioratedMudDensity: new UntypedFormControl(null),
      isLeakAtHanger: new UntypedFormControl(false)
    },
    toFormGroup: (obj: GeneralEPP): GeneralEPPForm => {
      return obj;
    },
    fromFormGroup: (formValue: GeneralEPPForm): GeneralEPP => {
      return formValue;
    }
  });

  constructor(
    private _trajectoryService: TrajectoryService,
    private _loadCaseService: LoadCaseService
  ) { }

  ngOnInit(): void {
    this.topPressureLabel = this.currentString['type'] === 'Casing' ? 'Surface Pressure' : 'Hanger Pressure';
    this.pressureLabel = unitsLib[this.userUnits.pressure].symbol;
    this.densityLabel = unitsLib[this.userUnits.density].symbol;
  }

  ngAfterContentInit(): void {
    setTimeout(() => {
      if (!this.form.formGroup.controls.fluidDensity.value) {
        this.form.formGroup.controls.fluidDensity.setValue(this.currentString?.annularFluid.state.nominalDensity);
      }
      if (!this.form.formGroup.controls.deterioratedMudDensity.value) {
        const defaultBaseFluidDensity = this.getBaseFluidDensity();
        this.form.formGroup.controls.deterioratedMudDensity.setValue(defaultBaseFluidDensity);
      }
    }, 100);

    this.hasDeterioratedMudChange();

    if (this.form.formGroup.controls.isLeakAtHanger.value) {
      this._loadCaseService.getPressuresAndTemperatures(this.loadCaseIndex).subscribe(pressTemp => {
        this.tubingLeakDensity = pressTemp.externalPressurePoints[0].pressure.toFixed(2);
      });
    }
  }

  private getBaseFluidDensity(): number {
    const fluidState = this.currentString?.annularFluid.state;
    if (fluidState.type !== 'standardMud') {
      return fluidState.nominalDensity;
    }

    switch (fluidState.baseFluid) {
      case 'freshwater':
        return GetValueFromPpg(8.33, this.densityLabel);
      case 'seawater':
        return GetValueFromPpg(8.6, this.densityLabel);
      case 'no2Diesel':
        return GetValueFromPpg(7, this.densityLabel);
      default:
        return fluidState.nominalDensity || 8.33;
    }
  }

  async ngOnChanges() {
      switch (this.userUnits.pressure) {
        case 'psi':
          this.pressureValidation = { min: 0, max: 30000 };
          break;
        case 'bar':
          this.pressureValidation = { min: 0, max: 2068 };
          break;
        case 'KPa':
          this.pressureValidation = { min: 0, max: 206800 };
          break;
        case 'atm':
          this.pressureValidation = { min: 0, max: 2041 };
          break;
      }
      this.form.formGroup.controls.hangerPressure.setValidators([Validators.required, Validators.min(this.pressureValidation.min), Validators.max(this.pressureValidation.max)]);
  
      switch (this.userUnits.density) {
        case 'ppg (U.S.)':
          this.densityValidation = { min: 0.01, max: 25 };
          break;
        case 'kg/m³':
        case 'g/L':
          this.densityValidation = { min: 1, max: 2500 };
          break;
        case 'g/cm³':
        case 'kg/l':
        case 'sg':
          this.densityValidation = { min: 0.00119, max: 3 };
          break;
        case 'g/m³':
          this.densityValidation = { min: 1198, max: 2995660 };
          break;
      }
      this.form.formGroup.controls.fluidDensity.setValidators([Validators.required, Validators.min(this.densityValidation.min), Validators.max(this.densityValidation.max)]);
      this.form.formGroup.controls.deterioratedMudDensity.setValidators([Validators.required, Validators.min(this.densityValidation.min), Validators.max(this.densityValidation.max)]);
      
      const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([this.currentString.hangerMd]));
      this.defaultHangerPressure = GetMudHydrostaticPressure(tvds[0], this.currentString?.annularFluid.state.nominalDensity, this.userUnits, true);
      if (this.form.formGroup.controls.hangerPressure.value == null || isNaN(this.form.formGroup.controls.hangerPressure.value) && isNumber(this.defaultHangerPressure)) {
        this.form.formGroup.controls.hangerPressure.setValue(this.defaultHangerPressure);
      }
  }

  public hasDeterioratedMudChange() {
    if (this.form.formGroup.controls.hasDeterioratedMud.value) {
      this.hasDeterioratedMud();
    } else {
      this.doesntHaveDeterioratedMud();
    }
  }

  private hasDeterioratedMud() {
    this.form.formGroup.controls.hasDeterioratedMud.setValue(true);
    this.form.formGroup.controls.deterioratedMudDensity.enable();
  }

  private doesntHaveDeterioratedMud() {
    this.form.formGroup.controls.hasDeterioratedMud.setValue(false);
    this.form.formGroup.controls.deterioratedMudDensity.disable();
  }

  onInputFocus($event) {
    $event.target.select();
  }
}
